import { Color as PersonaColor, Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons/24px';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import EditAndRulesBlock from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/EditAndRulesBlock';
import FormError from 'dashboard/components/FormControls/FormError';
import { ConditionalRule, LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { FieldSchemaType } from 'lib/fields';
import { JsonSchema } from 'lib/json-schema';

const RulesBlock = styled.div<{ nestedLayer: 'outer' | 'inner' }>`
  background: ${PersonaColor.BackgroundSecondary};
  margin-bottom: ${Spacing[2]};
  padding: ${({ nestedLayer }) =>
    nestedLayer === 'outer'
      ? `${Spacing[4]} ${Spacing[2]}`
      : `${Spacing[2]} ${Spacing[0]} ${Spacing[2]} ${Spacing[2]}`};
  border-radius: ${Spacing[1]};
`;

const RuleButtonBase = styled(Button).attrs({
  variant: 'basic',
  width: 'auto',
  prefixIcon: <PlusIcon />,
})`
  background: #00000000;
`;

const AddAndRuleButton = styled(RuleButtonBase)`
  color: ${PersonaColor.TextSecondary};
  margin-top: ${Spacing[2]};
  margin-left: ${Spacing[8]};
  svg,
  path {
    color: ${PersonaColor.TextSecondary};
  }
`;

const AddOrRuleButton = styled(RuleButtonBase)`
  color: ${PersonaColor.TextPrimary};
  margin-top: ${Spacing[1]};
  svg,
  path {
    color: ${PersonaColor.TextPrimary};
  }
`;

interface EditOrRulesBlockProps {
  rules: LogicBlockOrV2;
  jsonSchema: JsonSchema;
  operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>;
  prefixText: string;
  keyPrefix: string;
  nestedLayer?: 'outer' | 'inner';
  editable: boolean;
  enableValidation: boolean;
  addAndRule: (path: string[], nestedGroup?: boolean) => void;
  addOrRule: (path: string[]) => void;
  deleteRule: (orPath: string[], andIndex: number) => void;
  updateRule: (path: string[]) => (rule: ConditionalRule) => void;
  validateRule: (rule: ConditionalRule) => boolean;
}

const EditOrRulesBlock = ({
  rules,
  jsonSchema,
  operatorsMap,
  prefixText,
  keyPrefix,
  nestedLayer = 'outer',
  editable = true,
  enableValidation,
  addAndRule,
  addOrRule,
  deleteRule,
  updateRule,
  validateRule,
}: EditOrRulesBlockProps) => {
  const [orRulesValidityMap, setOrRulesValidityMap] = useState(
    rules.or?.reduce(
      (map, andRules, orIndex) => {
        return {
          ...map,
          [orIndex]: andRules.and.every((baseRule, _) => validateRule(baseRule)),
        };
      },
      {} as Record<number, boolean>,
    ),
  );
  const [areAllRulesValid, setAreAllRulesValid] = useState(
    Object.values(orRulesValidityMap).every((valid) => valid),
  );
  const showInvalidMessage = enableValidation && !areAllRulesValid;

  // Always stay up to date on which rules are valid
  useEffect(() => {
    const newValidityMap = rules.or.reduce(
      (map, andRules, orIndex) => {
        return {
          ...map,
          [orIndex]: andRules.and.every((baseRule, _) => validateRule(baseRule)),
        };
      },
      {} as Record<number, boolean>,
    );
    setOrRulesValidityMap(newValidityMap);
    const valid = Object.values(newValidityMap).every((valid) => valid);
    setAreAllRulesValid(valid);
  }, [rules]);

  return (
    <>
      {rules.or?.map((andRules, orIndex) => {
        // for Every Or rules block, start the block with "If" if it is the first rule
        // or "Or" for every following rule block
        //
        // If   LogicBlock
        // Or        LogicBlock
        // Or        LogicBlock
        const ruleUpdatePath = [`${orIndex}`, 'and'];
        return (
          <RulesBlock nestedLayer={nestedLayer} key={`${keyPrefix}_${orIndex}_AndRules`}>
            <EditAndRulesBlock
              andRules={andRules.and}
              jsonSchema={jsonSchema}
              operatorsMap={operatorsMap}
              orPrefixText={orIndex === 0 ? prefixText : 'Or'}
              orIndex={orIndex}
              editable={editable}
              enableValidation={enableValidation}
              addAndRule={(path) => addAndRule([...ruleUpdatePath, ...path])}
              addOrRule={(path) => addOrRule(['or', ...ruleUpdatePath, ...path])}
              updateRule={updateRule}
              deleteRule={deleteRule}
              validateRule={validateRule}
            />
            <HStack>
              <AddAndRuleButton
                id='component-logic__add-and-rule-button'
                disabled={!editable}
                onClick={() => addAndRule(ruleUpdatePath)}
              >
                Add
              </AddAndRuleButton>
              {nestedLayer === 'outer' && (
                <AddAndRuleButton
                  id='component-logic__add-nested-and-rule-button'
                  disabled={!editable}
                  onClick={() => addAndRule(ruleUpdatePath, true)}
                >
                  Add Group
                </AddAndRuleButton>
              )}
            </HStack>
          </RulesBlock>
        );
      })}
      {showInvalidMessage && <FormError>Conditions are not valid</FormError>}
      <AddOrRuleButton
        id='component-logic__add-or-rule-button'
        disabled={!editable}
        onClick={() => addOrRule(['or'])}
      >
        Or
      </AddOrRuleButton>
    </>
  );
};

export default EditOrRulesBlock;
